import { useMutation, useSuspenseQuery } from '@apollo/client';
import { NumericTextFieldController } from '@components/controllers/NumericTextFieldController';
import { DeviceTabProps } from '@features/deviceDataConfigurationModal/components/tabs/types';
import UPDATE_DEVICE_CALIBRATION_POSITION_FROM_ANGLE from '@features/deviceDataConfigurationModal/graphql/mutations/updateDeviceCalibrationPositionFromAngle';
import GET_DEVICE_POSITION_FROM_ANGLE_CALIBRATION from '@features/deviceDataConfigurationModal/graphql/queries/getDevicePositionFromAngleCalibration';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  InputAdornment,
  Typography,
} from '@mui/material';
import { useEffect, type FC } from 'react';
import { useForm } from 'react-hook-form';
import { UpdateDeviceCalibrationPositionFromAngleInput } from 'src/__generated__/graphql';
import { useModalStatusMessage } from 'src/hooks';
import { z } from 'zod';
import PositionFromAngleFormula from './PositionFromAngleFormula';

const schema = () => {
  return z.object({
    deviceId: z.string(),
    positionFromAngle: z.object({
      K: z.number().nullish(),
      R: z.number().nullish(),
      P: z.number().nullish(),
    }),
  }) satisfies z.ZodType<UpdateDeviceCalibrationPositionFromAngleInput>;
};

type FormValues = z.infer<ReturnType<typeof schema>>;

const PositionFromAngleCalibrationForm: FC<DeviceTabProps> = ({
  deviceId,
  onClose,
  setIsDirty,
}) => {
  const { data } = useSuspenseQuery(GET_DEVICE_POSITION_FROM_ANGLE_CALIBRATION, {
    variables: { deviceId },
  });
  const { _ } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const defaultValues = {
    deviceId: deviceId,
    positionFromAngle: {
      K: data.device.dataConfiguration.calibration?.positionFromAngle?.K ?? null,
      R: data.device.dataConfiguration.calibration?.positionFromAngle?.R ?? null,
      P: data.device.dataConfiguration.calibration?.positionFromAngle?.P ?? null,
    },
  };

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(schema()),
  });

  useEffect(() => setIsDirty(methods.formState.isDirty), [methods.formState.isDirty, setIsDirty]);

  const [update, { loading: updateLoading }] = useMutation(
    UPDATE_DEVICE_CALIBRATION_POSITION_FROM_ANGLE,
    {
      onCompleted: ({ updateDeviceCalibrationPositionFromAngle: { calibration } }) => {
        openModalSuccessMessage(_(msg`Datos actualizados correctamente`));
        const { positionFromAngle } = calibration ?? {};
        const { K, R, P } = positionFromAngle ?? {};
        methods.reset({
          deviceId,
          positionFromAngle: {
            K: K ?? null,
            R: R ?? null,
            P: P ?? null,
          },
        });
      },
      onError: (errors) => {
        openModalErrorMessage(_(msg`Error al actualizar los datos: ${errors.message}`));
      },
    },
  );

  if (!data.device.profile.sensor?.includes('G')) return null;

  const { isDirty, isSubmitting } = methods.formState;

  const submitHandler = async (formData: FormValues) => {
    update({ variables: { input: formData } });
  };

  return (
    <form onSubmit={methods.handleSubmit(submitHandler)}>
      <DialogContent
        sx={{
          maxWidth: 'md',
          minHeight: 500,
          margin: 'auto',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 0,
        }}
      >
        <Typography variant="h5" mb={2}>
          <Trans>Calibración posición</Trans>
        </Typography>
        <Box display="flex" gap={2}>
          <NumericTextFieldController
            control={methods.control}
            name="positionFromAngle.K"
            label="K"
            slotProps={{
              textField: {
                InputProps: {
                  endAdornment: <InputAdornment position="end">°</InputAdornment>,
                },
              },
            }}
          />
          <NumericTextFieldController
            control={methods.control}
            name="positionFromAngle.R"
            label="R"
            slotProps={{
              textField: {
                InputProps: {
                  endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                },
              },
            }}
          />
          <NumericTextFieldController
            control={methods.control}
            name="positionFromAngle.P"
            label="P"
            slotProps={{
              textField: {
                InputProps: {
                  endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                },
              },
            }}
          />
        </Box>
        <PositionFromAngleFormula />
      </DialogContent>
      <DialogActions sx={{ maxWidth: 'md', margin: 'auto', py: 2 }}>
        <Button
          disabled={!isDirty}
          color="info"
          variant="outlined"
          onClick={() => methods.reset(defaultValues)}
        >
          <Trans>Restablecer Formulario</Trans>
        </Button>
        <Box flexGrow={1} />
        <Button color="info" variant="outlined" onClick={() => onClose()}>
          <Trans>Cerrar</Trans>
        </Button>
        <LoadingButton
          disabled={!isDirty}
          loading={isSubmitting || updateLoading}
          type="submit"
          variant="contained"
        >
          <Trans>Guardar</Trans>
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

export default PositionFromAngleCalibrationForm;
